/**
 * TaskDetailsSheetContent Component
 *
 * Card-based content for the mobile bottom sheet.
 * Displays task details in styled cards matching the drawer layout pattern.
 *
 * Cards:
 * - Details (status, urgency, dates, metadata)
 * - Assigned To (assignee display + collaborators)
 * - Followers (overlapping avatar stack)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Select, Tooltip, message } from 'antd';
import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  LinkOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import { urgencyColors } from '../../configs/task.config';
import { neutral, status as statusTokens } from '../../configs/designTokens';
import '../../styles/index.scss';

const { Option } = Select;

/**
 * Maps a task status string like 'IN_PROGRESS' to a camelCase key
 * and returns the corresponding color token.
 */
const getStatusColor = (taskStatus) => {
  if (!taskStatus) return neutral[400];
  const key = taskStatus
    .toLowerCase()
    .replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return statusTokens[key] || neutral[400];
};

const TaskDetailsSheetContent = ({
  currentTask,
  currentUser,
  currentTaskId,
  isFollowers,
  isCurrentUserAssignee,
  isCollaborator,
  updateTaskLoading,
  updateTask,
  refetchTask,
  onFollowChange,
  onCollaboratorsUpdate,
  level,
  // Inline assignee editing
  isEditingAssignee,
  setIsEditingAssignee,
  selectedOrg,
  setSelectedOrg,
  selectedAssignees,
  setSelectedAssignees,
  fetchBasicOrgs,
  fetchBasicUsers,
  basicOrgsDetails,
  basicOrgsDetailsLoading,
  basicUsersDetails,
  basicUsersDetailsLoading,
  onUserUpdate,
}) => {
  if (!currentTask) return null;

  const assignee = currentTask.assigneeUsers?.[0];
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleFollowToggle = async () => {
    const createdBy = currentUser.id === currentTask?.createdBy?.id;

    if (!isFollowers && (isCurrentUserAssignee || isCollaborator || createdBy)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees already follow tasks by default.');
      } else if (isCollaborator) {
        message.warning('Collaborators already follow tasks by default.');
      } else if (createdBy) {
        message.warning('You are the creator and already follow by default.');
      }
      return;
    }

    onFollowChange(!isFollowers);

    try {
      await updateTask({
        variables: {
          data: {
            followers: !isFollowers
              ? { connect: { id: currentUser.id } }
              : { disconnect: { id: currentUser.id } },
          },
          where: { id: currentTaskId },
        },
      });
      refetchTask();
      message.success(!isFollowers ? 'You followed the task.' : 'Unfollowed task.');
    } catch (error) {
      message.error(!isFollowers ? 'Failed to follow.' : 'Failed to unfollow.');
    }
  };

  const handleExternalLink = () => {
    const url = currentTask?.linkUrl;
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="task-sheet-content">
      {/* ── Details Card ── */}
      <div className="task-sheet-content__card">
        <div className="task-sheet-content__card-header">
          <div className="task-sheet-content__card-title">
            <ClockCircleOutlined className="task-sheet-content__card-icon" />
            <span>Details</span>
          </div>
          {currentTask.linkUrl && (
            <Button
              type="text"
              size="small"
              onClick={handleExternalLink}
              className="task-sheet-content__link-btn"
            >
              <LinkOutlined /> Link
            </Button>
          )}
        </div>

        <div className="task-sheet-content__details-grid">
          {/* Status */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Status</span>
            <span className="task-sheet-content__detail-value">
              <span
                className="task-sheet-content__status-dot"
                style={{ backgroundColor: getStatusColor(currentTask.status) }}
              />
              {startCase(currentTask.status?.toLowerCase().replace(/_/g, ' ') || 'Unknown')}
            </span>
          </div>

          {/* Urgency */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Urgency</span>
            <span className="task-sheet-content__detail-value">
              {currentTask.urgency ? (
                <>
                  <span
                    className="task-sheet-content__status-dot"
                    style={{ backgroundColor: urgencyColors[currentTask.urgency] }}
                  />
                  {startCase(currentTask.urgency.toLowerCase())}
                </>
              ) : (
                '-'
              )}
            </span>
          </div>

          {/* Due Date */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Due Date</span>
            <span className="task-sheet-content__detail-value">
              <CalendarOutlined />
              {formatDate(currentTask.dueDate)}
              {currentTask.recurring && (
                <SyncOutlined className="task-sheet-content__recurring" />
              )}
            </span>
          </div>

          {/* Start Date */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Start Date</span>
            <span className="task-sheet-content__detail-value">
              {formatDate(currentTask.startDate)}
            </span>
          </div>

          {/* Created */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Created</span>
            <span className="task-sheet-content__detail-value">
              {formatDate(currentTask.createdAt)}
            </span>
          </div>

          {/* Requested By */}
          <div className="task-sheet-content__detail">
            <span className="task-sheet-content__detail-label">Requested By</span>
            <span className="task-sheet-content__detail-value">
              {startCase(currentTask.createdBy?.name || currentTask.createdBy?.username || 'Unknown')}
            </span>
          </div>

          {/* Channel Partner (conditional) */}
          {currentTask.channelPartner && (
            <div className="task-sheet-content__detail">
              <span className="task-sheet-content__detail-label">Channel Partner</span>
              <span className="task-sheet-content__detail-value">
                {currentTask.channelPartner.name}
              </span>
            </div>
          )}

          {/* Advertiser (conditional) */}
          {currentTask.advertiser && (
            <div className="task-sheet-content__detail">
              <span className="task-sheet-content__detail-label">Advertiser</span>
              <span className="task-sheet-content__detail-value">
                {currentTask.advertiser.name}
              </span>
            </div>
          )}

          {/* Request Type (conditional, full-width) */}
          {currentTask.requestType && (
            <div className="task-sheet-content__detail task-sheet-content__detail--full">
              <span className="task-sheet-content__detail-label">Request Type</span>
              <span className="task-sheet-content__detail-value">
                {currentTask.requestType}
                {currentTask.requestSubType && ` → ${currentTask.requestSubType}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Assignees Card ── */}
      <div className="task-sheet-content__card">
        <div className="task-sheet-content__card-header">
          <div className="task-sheet-content__card-title">
            <UserOutlined className="task-sheet-content__card-icon task-sheet-content__card-icon--success" />
            <span>Assigned To</span>
          </div>
          {!isEditingAssignee && level === 'PLATFORM' && (
            <Button
              type="text"
              size="small"
              className="task-sheet-content__action-btn"
              onClick={() => {
                setIsEditingAssignee(true);
                fetchBasicOrgs();
              }}
            >
              <EditOutlined /> Edit
            </Button>
          )}
        </div>

        {/* Assignee Display (non-editing) */}
        {!isEditingAssignee && assignee && (
          <div className="task-sheet-content__assignee-display">
            <AvatarS3
              imgKey={assignee.avatar?.key}
              username={assignee.name || assignee.username}
              avatarProps={{ size: 32 }}
            />
            <div className="task-sheet-content__assignee-info">
              <div className="task-sheet-content__assignee-name">
                {startCase(assignee.name || assignee.username)}
              </div>
              {currentTask.assigneeOrg?.name && (
                <div className="task-sheet-content__assignee-org">
                  {currentTask.assigneeOrg.name}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Unassigned (non-editing) */}
        {!isEditingAssignee && !assignee && (
          <p className="task-sheet-content__empty-text">
            No one assigned yet.
          </p>
        )}

        {/* Editing Mode */}
        {isEditingAssignee && (
          <div className="task-sheet-content__assignment">
            {!currentTask.project && (
              <div className="task-sheet-content__detail">
                <span className="task-sheet-content__detail-label">Organization</span>
                <Select
                  placeholder="Select Organization"
                  loading={basicOrgsDetailsLoading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onSearch={(value) => fetchBasicOrgs(value)}
                  value={selectedOrg}
                  onChange={(value) => {
                    setSelectedOrg(value);
                    setSelectedAssignees([]);
                  }}
                  style={{ width: '100%' }}
                  size="small"
                >
                  {basicOrgsDetails.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            <div className="task-sheet-content__detail">
              <span className="task-sheet-content__detail-label">
                Assignee
                {currentTask.project && (
                  <span className="task-sheet-content__detail-hint">(from project)</span>
                )}
              </span>
              {!currentTask.project ? (
                <Select
                  placeholder="Select Assignee"
                  loading={basicUsersDetailsLoading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onSearch={(value) => fetchBasicUsers(value)}
                  value={selectedAssignees}
                  onChange={(value) => setSelectedAssignees(value)}
                  style={{ width: '100%' }}
                  size="small"
                  disabled={!selectedOrg}
                >
                  {basicUsersDetails.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.name || user.username}
                    </Option>
                  ))}
                </Select>
              ) : (
                <span className="task-sheet-content__detail-value">
                  {currentTask.assigneeUsers?.length > 0
                    ? currentTask.assigneeUsers.map((u) => startCase(u.name || u.username)).join(', ')
                    : '-'}
                </span>
              )}
            </div>

            <div className="task-sheet-content__edit-actions">
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setIsEditingAssignee(false);
                  setSelectedOrg(currentTask.assigneeOrg?.id || null);
                  setSelectedAssignees(
                    currentTask.assigneeUsers?.[0]?.id || []
                  );
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={updateTaskLoading}
                onClick={onUserUpdate}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Collaborators */}
        <div className="task-sheet-content__collaborators">
          <div className="task-sheet-content__card-header">
            <div className="task-sheet-content__card-title">
              Collaborators
              {currentTask.collaborators?.length > 0 && (
                <span className="task-sheet-content__count">
                  ({currentTask.collaborators.length})
                </span>
              )}
            </div>
            {level === 'PLATFORM' && (
              <Button
                type="text"
                size="small"
                onClick={onCollaboratorsUpdate}
                disabled={updateTaskLoading}
                className={`task-sheet-content__action-btn ${isCollaborator ? 'task-sheet-content__action-btn--leave' : 'task-sheet-content__action-btn--join'}`}
              >
                {isCollaborator ? (
                  <>
                    <MinusOutlined /> Leave
                  </>
                ) : (
                  <>
                    <PlusOutlined /> Join
                  </>
                )}
              </Button>
            )}
          </div>

          {currentTask.collaborators?.length > 0 ? (
            <div className="task-sheet-content__avatar-stack">
              {currentTask.collaborators.map((collaborator) => (
                <Tooltip
                  key={collaborator.id}
                  title={startCase(collaborator.name || collaborator.username)}
                >
                  <div className="task-sheet-content__avatar-item">
                    <AvatarS3
                      imgKey={collaborator.avatar?.key}
                      username={collaborator.name || collaborator.username}
                      avatarProps={{ size: 28 }}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : (
            <p className="task-sheet-content__empty-text">
              No collaborators. Join to help with this task.
            </p>
          )}
        </div>
      </div>

      {/* ── Followers Card ── */}
      <div className="task-sheet-content__card">
        <div className="task-sheet-content__card-header">
          <div className="task-sheet-content__card-title">
            <EyeOutlined className="task-sheet-content__card-icon task-sheet-content__card-icon--primary" />
            <span>Followers</span>
            {currentTask.followers?.length > 0 && (
              <span className="task-sheet-content__count">
                ({currentTask.followers.length})
              </span>
            )}
          </div>
          <Button
            type="text"
            size="small"
            onClick={handleFollowToggle}
            disabled={updateTaskLoading}
            className={`task-sheet-content__action-btn ${isFollowers ? 'task-sheet-content__action-btn--unfollow' : 'task-sheet-content__action-btn--follow'}`}
          >
            {isFollowers ? (
              <>
                <MinusOutlined /> Unfollow
              </>
            ) : (
              <>
                <PlusOutlined /> Follow
              </>
            )}
          </Button>
        </div>

        {currentTask.followers?.length > 0 ? (
          <div className="task-sheet-content__avatar-stack">
            {currentTask.followers.slice(0, 8).map((follower) => (
              <Tooltip
                key={follower.id}
                title={startCase(follower.name || follower.username)}
              >
                <div className="task-sheet-content__avatar-item">
                  <AvatarS3
                    imgKey={follower.avatar?.key}
                    username={follower.name || follower.username}
                    avatarProps={{ size: 28 }}
                  />
                </div>
              </Tooltip>
            ))}
            {currentTask.followers.length > 8 && (
              <div className="task-sheet-content__avatar-more">
                +{currentTask.followers.length - 8}
              </div>
            )}
          </div>
        ) : (
          <p className="task-sheet-content__empty-text">
            No followers yet. Follow to receive updates.
          </p>
        )}
      </div>
    </div>
  );
};

TaskDetailsSheetContent.propTypes = {
  currentTask: PropTypes.object,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  currentTaskId: PropTypes.string.isRequired,
  isFollowers: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isCollaborator: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  updateTask: PropTypes.func.isRequired,
  refetchTask: PropTypes.func.isRequired,
  onFollowChange: PropTypes.func.isRequired,
  onCollaboratorsUpdate: PropTypes.func.isRequired,
  level: PropTypes.string,
  isEditingAssignee: PropTypes.bool,
  setIsEditingAssignee: PropTypes.func,
  selectedOrg: PropTypes.string,
  setSelectedOrg: PropTypes.func,
  selectedAssignees: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  setSelectedAssignees: PropTypes.func,
  fetchBasicOrgs: PropTypes.func,
  fetchBasicUsers: PropTypes.func,
  basicOrgsDetails: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetails: PropTypes.array,
  basicUsersDetailsLoading: PropTypes.bool,
  onUserUpdate: PropTypes.func,
};

TaskDetailsSheetContent.defaultProps = {
  currentTask: null,
  isFollowers: false,
  isCurrentUserAssignee: false,
  isCollaborator: false,
  updateTaskLoading: false,
  level: null,
  isEditingAssignee: false,
  setIsEditingAssignee: () => {},
  selectedOrg: null,
  setSelectedOrg: () => {},
  selectedAssignees: [],
  setSelectedAssignees: () => {},
  fetchBasicOrgs: () => {},
  fetchBasicUsers: () => {},
  basicOrgsDetails: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetails: [],
  basicUsersDetailsLoading: false,
  onUserUpdate: () => {},
};

export default TaskDetailsSheetContent;
